/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2024 Open Assessment Technologies SA;
 */
import { debounce } from 'lodash';

/**
 * Creates history manipulating functions and closures the values
 *
 * @param {String} existingHistory history,to initialize with
 * @param {Function} storingCallback callback,  to perform history storing
 * @param {Number} historyLimit number of externally stored history entires
 * @param {Number} historySaveMinWait (ms) minimum time between history save actions
 * @param {Number} historySaveMaxWait (ms) maximum time between history save actions
 *
 * @returns {Object}
 */
export default function historyManagerFactory(
    existingHistory = [],
    storingCallback,
    historyLimit = 10,
    // Unified with test-runner localItemState plugin
    // 2 and 5 sec
    historySaveMinWait = 2000,
    historySaveMaxWait = 5000
) {
    /* Limit of internal history stack, that is not persistent
     * It's unlikely to be reached while editing, but it's better to
     * have boundary on stack */
    let editorHistory = existingHistory;

    /**
     * Stores history in the state
     *
     * @param {string} parsedValue current input string
     */
    function storeHistory(parsedValue) {
        // There's no need to store identical history steps
        if (!editorHistory.length || parsedValue !== editorHistory[editorHistory.length - 1]?.base?.string) {
            editorHistory = [
                ...editorHistory,
                {
                    base: {
                        string: parsedValue
                    }
                }
            ];
            storingCallback(editorHistory.slice(-historyLimit));
        }
    }

    const throttledStoreHistory = debounce(storeHistory, historySaveMinWait, {
        maxWait: historySaveMaxWait
    });

    const destroy = () => throttledStoreHistory?.cancel();

    /**
     * Stores history in the state
     *
     * @param {string} data current input in editor's textarea
     */
    function processInputForHistory(data) {
        throttledStoreHistory(data);
    }

    return {
        processInputForHistory,
        destroy
    };
}
